import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { ArrowLeft, Loader2, DollarSign, Clock, Shield, TrendingUp, Car, Smartphone } from 'lucide-react';
import { toast } from '../hooks/use-toast';
import axios from 'axios';

const DriverLogin = () => {
  const navigate = useNavigate();
  const logoUrl = 'https://customer-assets.emergentagent.com/job_transport-app-29/artifacts/c2qih6ut_J%20%26%20S%20Logo%2001%20%20%282%29_1024x1024.jpg';
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      const response = await axios.post(`${BACKEND_URL}/api/drivers/login?email=${encodeURIComponent(email)}&password=${encodeURIComponent(password)}`);
      
      // Store driver info in localStorage
      localStorage.setItem('driver_id', response.data.driver_id);
      localStorage.setItem('driver_name', response.data.name);
      localStorage.setItem('driver_email', response.data.email);
      localStorage.setItem('driver_status', response.data.status);
      
      toast({
        title: "Welcome Back, Driver!",
        description: `Ready to earn, ${response.data.name}!`,
      });
      
      setTimeout(() => navigate('/driver'), 500);
    } catch (error) {
      console.error('Login error:', error);
      toast({
        title: "Login Failed",
        description: error.response?.data?.detail || "Invalid credentials. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const benefits = [
    { icon: DollarSign, title: "Earn Big", desc: "Competitive pay + 100% tips" },
    { icon: Clock, title: "Flexible Hours", desc: "Work when you want" },
    { icon: TrendingUp, title: "Weekly Payouts", desc: "Fast PayPal transfers" },
    { icon: Shield, title: "Full Support", desc: "24/7 driver assistance" },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-900 via-slate-800 to-slate-900 flex">
      {/* Left Side - Branding & Benefits */}
      <div className="hidden lg:flex lg:w-1/2 flex-col justify-center items-center p-12 text-white relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-20 w-72 h-72 bg-blue-500 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-amber-500 rounded-full blur-3xl"></div>
        </div>
        
        <div className="relative z-10 max-w-md">
          <div className="flex items-center gap-4 mb-8">
            <img src={logoUrl} alt="J-Style Services" className="h-20 w-20 object-contain" />
            <div className="h-12 w-px bg-gray-600"></div>
            <div className="flex items-center gap-2">
              <Car size={32} className="text-amber-400" />
              <span className="text-2xl font-bold text-gray-300">Driver Portal</span>
            </div>
          </div>
          
          <h1 className="text-4xl font-bold mb-4">
            <span className="text-white">Drive with </span>
            <span className="bg-gradient-to-r from-amber-400 to-yellow-300 bg-clip-text text-transparent">
              J-Style
            </span>
          </h1>
          <p className="text-xl text-gray-300 mb-10">
            Join our team of professional drivers and start earning today
          </p>
          
          <div className="grid grid-cols-2 gap-4">
            {benefits.map((benefit, index) => (
              <div key={index} className="bg-white/5 backdrop-blur rounded-xl p-4 border border-white/10">
                <div className="w-10 h-10 bg-amber-500/20 rounded-lg flex items-center justify-center mb-3">
                  <benefit.icon size={20} className="text-amber-400" />
                </div>
                <h3 className="font-semibold text-white mb-1">{benefit.title}</h3>
                <p className="text-sm text-gray-400">{benefit.desc}</p>
              </div>
            ))}
          </div>

          <div className="mt-10 p-4 bg-gradient-to-r from-amber-500/20 to-yellow-500/20 rounded-xl border border-amber-500/30">
            <div className="flex items-center gap-3">
              <Smartphone className="text-amber-400" size={24} />
              <div>
                <p className="font-semibold text-white">Get instant ride notifications</p>
                <p className="text-sm text-gray-300">Real-time alerts when rides are available</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Right Side - Login Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-6 lg:p-12">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="lg:hidden text-center mb-8">
            <img src={logoUrl} alt="Logo" className="h-16 w-16 mx-auto mb-4 object-contain" />
            <div className="flex items-center justify-center gap-2 mb-2">
              <Car size={24} className="text-amber-400" />
              <h1 className="text-2xl font-bold text-white">Driver Portal</h1>
            </div>
          </div>

          <Card className="p-8 border-0 shadow-2xl bg-white/95 backdrop-blur">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                <Car size={32} className="text-white" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Driver Login</h2>
              <p className="text-gray-500">Sign in to start accepting rides</p>
            </div>

            <form onSubmit={handleLogin} className="space-y-5">
              <div>
                <Label htmlFor="email" className="text-gray-700">Email Address</Label>
                <Input 
                  id="email"
                  type="email"
                  placeholder="driver@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="mt-1.5 h-12 border-2 focus:border-blue-500"
                  data-testid="driver-login-email"
                />
              </div>

              <div>
                <Label htmlFor="password" className="text-gray-700">Password</Label>
                <Input 
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="mt-1.5 h-12 border-2 focus:border-blue-500"
                  data-testid="driver-login-password"
                />
              </div>

              <div className="flex items-center justify-between text-sm">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" className="rounded border-gray-300 text-blue-500 focus:ring-blue-500" />
                  <span className="text-gray-600">Remember me</span>
                </label>
                <button 
                  type="button" 
                  onClick={() => navigate('/driver/forgot-password?type=driver')}
                  className="text-blue-600 hover:text-blue-700 font-medium"
                  data-testid="forgot-password-link"
                >
                  Forgot password?
                </button>
              </div>

              <Button 
                type="submit"
                disabled={isLoading}
                className="w-full h-12 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-semibold text-lg shadow-lg"
                data-testid="driver-login-submit"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    Signing in...
                  </>
                ) : (
                  'Sign In to Drive'
                )}
              </Button>
            </form>

            <div className="mt-6 pt-6 border-t border-gray-100">
              <p className="text-center text-gray-600 mb-4">
                Want to become a driver?
              </p>
              <Button 
                variant="outline" 
                onClick={() => navigate('/driver/signup')}
                className="w-full h-12 border-2 border-amber-500 text-amber-600 hover:bg-amber-50 font-semibold"
              >
                Apply to Drive
              </Button>
            </div>
          </Card>

          {/* Back Button */}
          <div className="mt-6 text-center">
            <Button 
              variant="ghost" 
              onClick={() => navigate('/')} 
              className="text-gray-400 hover:text-white"
            >
              <ArrowLeft size={18} className="mr-2" />
              Back to Home
            </Button>
          </div>

          {/* Social Media Sharing Link Display */}
          <div className="mt-8 text-center">
            <p className="text-xs text-gray-500 mb-2">Share this link to recruit drivers:</p>
            <code className="text-xs bg-slate-700 text-blue-400 px-3 py-1.5 rounded-full">
              {window.location.origin}/driver/login
            </code>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DriverLogin;
